/* Print/PDF-friendly AI sections rendered inside the Property Analysis Report.
 *
 * Read-only views of the AI Lead Score and AI Valuation data already
 * persisted on the lead document — designed to match the report's
 * professional typography so the same component renders cleanly in:
 *   • The owner's `/app/leads/:id/report` view
 *   • The public `/r/{share_token}` page (buyers + JV partners)
 *   • The public snapshot page
 *   • Print / "Save as PDF" via the browser
 *
 * Renders nothing when the lead has neither an AI score nor an AI
 * valuation, so older reports stay clean.
 */
import { Flame, Snowflake, TrendingUp, Calculator, DollarSign, Sparkles } from "lucide-react";

const fmtMoney = (n) =>
  typeof n === "number" ? `$${Math.round(n).toLocaleString()}` : "—";

export default function AIReportSections({ lead }) {
  const hasScore = typeof lead?.ai_score === "number";
  const hasValuation = typeof lead?.ai_mao === "number" || typeof lead?.ai_arv_mid === "number";
  if (!hasScore && !hasValuation) return null;

  return (
    <section
      className="grid md:grid-cols-2 gap-5 mb-6 print:break-inside-avoid"
      data-testid="report-ai-sections"
    >
      {hasScore && <AIScoreBlock lead={lead} />}
      {hasValuation && <AIValuationBlock lead={lead} />}
    </section>
  );
}

/* ── AI Lead Score block ──────────────────────────────────────────── */
function AIScoreBlock({ lead }) {
  const score = lead.ai_score;
  const label = (lead.ai_score_label || "").toLowerCase();
  const Icon = label === "hot" ? Flame : label === "cold" ? Snowflake : TrendingUp;
  const tone =
    label === "hot"
      ? { card: "from-rose-50 to-amber-50 border-rose-200", icon: "text-rose-600", badge: "bg-rose-600" }
      : label === "warm"
      ? { card: "from-amber-50 to-yellow-50 border-amber-200", icon: "text-amber-600", badge: "bg-amber-500" }
      : label === "cold"
      ? { card: "from-slate-50 to-sky-50 border-slate-200", icon: "text-slate-500", badge: "bg-slate-400" }
      : { card: "from-emerald-50 to-white border-emerald-200", icon: "text-emerald-600", badge: "bg-emerald-600" };

  return (
    <div
      className={`card border-2 bg-gradient-to-br ${tone.card} print:break-inside-avoid`}
      data-testid="report-ai-score-block"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={11} className="text-emerald-600" />
        <span className="label-eyebrow">AI Deal Intelligence</span>
      </div>

      <div className="flex items-baseline gap-3 mb-4">
        <Icon size={22} className={tone.icon} />
        <div>
          <div className="font-display font-extrabold text-4xl tabular-nums leading-none">
            {score}
            <span className="text-base font-normal text-stone-500">/100</span>
          </div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mt-1">
            AI Lead Score
          </div>
        </div>
        {label && (
          <span
            className={`text-xs font-bold uppercase px-2.5 py-1 rounded ml-auto text-white ${tone.badge}`}
          >
            {label}
          </span>
        )}
      </div>

      {/* Breakdown bars */}
      {(lead.ai_score_motivation != null || lead.ai_score_urgency != null) && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <ScoreBar label="Motivation" value={lead.ai_score_motivation} color="bg-rose-500" />
          <ScoreBar label="Urgency" value={lead.ai_score_urgency} color="bg-amber-500" />
          <ScoreBar label="Equity" value={lead.ai_score_equity} color="bg-emerald-500" />
          <ScoreBar label="Reachable" value={lead.ai_score_contactability} color="bg-sky-500" />
        </div>
      )}

      {lead.ai_score_reasoning && (
        <div className="text-[13px] text-stone-700 leading-relaxed mb-2">
          {lead.ai_score_reasoning}
        </div>
      )}

      {lead.ai_score_next_action && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-white/70 border text-[13px]">
          <span className="font-bold text-stone-900">Next action: </span>
          <span className="text-stone-700">{lead.ai_score_next_action}</span>
        </div>
      )}
    </div>
  );
}

/* ── AI Valuation block (ARV + MAO) ──────────────────────────────── */
function AIValuationBlock({ lead }) {
  const confidence = (lead.ai_valuation_confidence || "").toLowerCase();
  const confBg =
    confidence === "high"
      ? "bg-emerald-100 text-emerald-800"
      : confidence === "low"
      ? "bg-rose-100 text-rose-800"
      : "bg-amber-100 text-amber-800";

  return (
    <div
      className="card border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 print:break-inside-avoid"
      data-testid="report-ai-valuation-block"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Calculator size={11} className="text-emerald-700" />
          <span className="label-eyebrow text-emerald-700">AI Valuation</span>
        </div>
        {confidence && (
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${confBg}`}>
            {confidence} confidence
          </span>
        )}
      </div>

      <div className="font-display font-bold text-lg mb-3 leading-tight">
        ARV + Max Allowable Offer (70% rule)
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat
          label="ARV mid"
          value={fmtMoney(lead.ai_arv_mid)}
          sub={
            lead.ai_arv_low && lead.ai_arv_high
              ? `${fmtMoney(lead.ai_arv_low)} – ${fmtMoney(lead.ai_arv_high)}`
              : null
          }
        />
        <Stat label="Repairs" value={fmtMoney(lead.ai_repair_estimate)} sub="rehab budget" />
        <Stat
          label="MAO"
          value={fmtMoney(lead.ai_mao)}
          sub="max offer"
          accent
          icon={<DollarSign size={11} />}
        />
      </div>

      {typeof lead.ai_arv_mid === "number" && (
        <div className="bg-white/70 border rounded-lg p-2.5 mb-3 font-mono text-[11px] text-stone-700 leading-relaxed">
          <span className="text-stone-500">MAO = </span>
          ({fmtMoney(lead.ai_arv_mid)} × 0.70) − {fmtMoney(lead.ai_repair_estimate)} −{" "}
          {fmtMoney(lead.ai_assignment_fee || 10000)}
          <span className="text-emerald-700 font-bold"> = {fmtMoney(lead.ai_mao)}</span>
        </div>
      )}

      {lead.ai_valuation_reasoning && (
        <div className="text-[13px] text-stone-700 leading-relaxed mb-2">
          {lead.ai_valuation_reasoning}
        </div>
      )}

      {Array.isArray(lead.ai_valuation_key_factors) && lead.ai_valuation_key_factors.length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-bold tracking-wide text-stone-500 mb-1.5">
            Key factors
          </div>
          <ul className="text-xs text-stone-700 space-y-1">
            {lead.ai_valuation_key_factors.map((f, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="text-emerald-600 shrink-0">•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ label, value, color }) {
  const pct = typeof value === "number" ? Math.max(0, Math.min(100, (value / 25) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-[10px] uppercase font-bold text-stone-500 mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{value ?? 0}/25</span>
      </div>
      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent, icon }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide text-stone-500 mb-1">
        {icon}
        {label}
      </div>
      <div
        className={
          "font-display font-bold tabular-nums " +
          (accent ? "text-2xl text-emerald-700" : "text-xl text-stone-900")
        }
      >
        {value}
      </div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}
